import React from 'react'
import PropTypes from 'prop-types'
import { Route, IndexRoute, IndexRedirect } from 'react-router'
import { AuthorizedRoute, Content } from 'components'
import LayoutAuthorized from './layout-authorized'
import { LEARNING_HUB_ROUTE, SIGN_IN_ROUTE } from 'config'
import getPortalAuthToken from 'utils/local/get-portal-auth-token'
import { routes as LearningHubHomeRoutes } from './home'
import { routes as CourseCatalogRoutes } from './course-catalog'
import { routes as MyCoursesRoutes } from './my-courses'
import { routes as MyProfileRoutes } from './my-profile'
import {
  PlaceholderPage,
  CoursePreviewShell,
  CourseStoryPage,
  ChapterOverviewPage,
  ChapterBlockPlayerPage,
} from './views'
import CourseFlowRoutes from './course-flow/routes'
import forumRoutes from './forum/routes'

function FaqPage() {
  return <PlaceholderPage title="FAQ" />
}

function isPortalAuthenticated() {
  return !!getPortalAuthToken()
}

// Lightweight layout for the admin-only "Preview Course" subtree.
//
// The course-preview tree sits OUTSIDE AuthorizedRoute/LayoutAuthorized
// because ActiveAdmin admins drive it without a portal JWT — the per-course
// preview token (see CoursePreviewShell) is the entire authentication
// surface. We still need an outer container so the player pages can paint
// their existing chrome (which assumes it lives inside the standard portal
// wrapper).
function PreviewLayout({ children }) {
  return (
    <div className="learning-hub learning-hub-course-preview-shell">
      <Content id="content" className="portal-wrapper">
        {children}
      </Content>
    </div>
  )
}
PreviewLayout.propTypes = { children: PropTypes.node }
PreviewLayout.defaultProps = { children: null }

// Inject the same `isPreviewMode` flag that CoursePreviewShell sets when
// the admin first lands on /learning-hub/course-preview?course_id=...&preview_token=...
// so the player components branch their fetches, Links, and side effects.
function withPreviewMode(Component) {
  function Wrapped(props) {
    return <Component {...props} isPreviewMode />
  }
  Wrapped.displayName = `withPreviewMode(${
    Component.displayName || Component.name || 'Component'
  })`
  return Wrapped
}

const PreviewCourseStoryPage = withPreviewMode(CourseStoryPage)
const PreviewChapterOverviewPage = withPreviewMode(ChapterOverviewPage)
const PreviewChapterBlockPlayerPage = withPreviewMode(ChapterBlockPlayerPage)

const Routes = (
  <Route path={LEARNING_HUB_ROUTE}>
    <IndexRedirect to="home" />
    {/*
      Admin "Preview Course" subtree.

      Entry: /learning-hub/course-preview?course_id=X&preview_token=Y
        CoursePreviewShell stashes the token in localStorage and redirects to
        /learning-hub/course-preview/course/X so subsequent navigation has a
        meaningful URL and React-Router params.
    */}
    <Route path="course-preview" component={PreviewLayout}>
      <IndexRoute component={CoursePreviewShell} />
      <Route path="course/:courseId" component={PreviewCourseStoryPage} />
      <Route
        path="course/:courseId/chapters"
        component={PreviewChapterOverviewPage}
      />
      <Route
        path="course/:courseId/chapters/:chapterId/blocks/:blockId"
        component={PreviewChapterBlockPlayerPage}
      />
    </Route>
    <AuthorizedRoute
      authFunction={isPortalAuthenticated}
      redirect={SIGN_IN_ROUTE}
    >
      <Route component={LayoutAuthorized}>
        {LearningHubHomeRoutes}
        {CourseCatalogRoutes}
        {MyCoursesRoutes}
        {MyProfileRoutes}
        {CourseFlowRoutes}
        {forumRoutes}
        <Route path="faq" component={FaqPage} />
      </Route>
    </AuthorizedRoute>
  </Route>
)

export default Routes
